import { Button, Tooltip } from "@xola/ui-kit";
import { useUnmount } from "ahooks";
import React from "react";
import { useHistory } from "react-router-dom";
import { Merchandise } from "types";
import shallow from "zustand/shallow";
import { ContentLayout } from "../../Layout/ContentLayout";
import { CheckoutButton } from "../CheckoutButton";
import { CreatePurchaseFooter } from "../CreatePurchaseFooter";
import { CreatePurchaseMerchandiseFields } from "../CreatePurchaseMerchandiseFields";
import { CreatePurchaseSidebar } from "../CreatePurchaseSidebar";
import { getMerchandiseValidations } from "../hooks/getMerchandiseValidations";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { getPurchaseItemsHavingId } from "@/components/PurchaseCreate/helpers/PurchaseCreateHelpers";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface CreatePurchaseMerchandiseScreenProps {
    merchandise: Merchandise;
    onSubmit: () => void;
}

export const CreatePurchaseMerchandiseScreen = ({ merchandise, onSubmit }: CreatePurchaseMerchandiseScreenProps) => {
    const history = useHistory();

    const popoverParentId = `popover-${merchandise.id}`;

    useUnmount(() => {
        const isSingleItemPurchase = !useCreatePurchaseStore.getState().isMist();
        if (history.action === "POP" && isSingleItemPurchase) {
            /** User has clicked on a product in create purchase flow and clicked browser back button, so we need to reset the CreatePurchase state to initial */
            useCreatePurchaseStore.getState().actions.resetState();
        }
    });

    return (
        <div className="grid h-full grid-cols-12" id={popoverParentId}>
            <ContentLayout isWide classNames={{ root: "relative col-span-6 h-full 2xl:col-span-7" }}>
                <h3 className="mb-6">Product Details</h3>

                <CreatePurchaseMerchandiseFields merchandise={merchandise} />

                <CreateMerchandisePurchaseFooter onSubmit={onSubmit} />
            </ContentLayout>

            <div className="col-span-6 shrink-0 space-y-6 bg-gray-lighter 2xl:col-span-5">
                <CreatePurchaseSidebar />
            </div>
        </div>
    );
};

const CreateMerchandisePurchaseFooter = ({ onSubmit }: { onSubmit: () => void }) => {
    const history = useHistory();
    const shouldDisableFooter = useCreatePurchaseStore((state) => !state.purchaseId);
    const showNextButton = useCreatePurchaseStore((state) => state.isMist());
    const [activeItemIndex, itemCount] = useCreatePurchaseStore(
        (state) => [state.activeItemIndex, state.purchase.items.length, state.getActiveItem()?.id],
        shallow,
    );

    const [setActiveItemIndex, setPurchase, resetState] = useCreatePurchaseStore(
        (state) => [state.actions.setActiveItemIndex, state.actions.setPurchase, state.actions.resetState],
        shallow,
    );

    const { getDisabledReasons } = getMerchandiseValidations();
    const disabledReasons = getDisabledReasons();
    const hasDisabledReasons = disabledReasons.length > 0;
    const shouldShowNextButton = showNextButton && activeItemIndex < itemCount - 1;
    const buttonLabel = shouldShowNextButton ? "Next" : "Checkout";

    const handleCancel = () => {
        resetState();
        history.push("/cart/init");
    };

    const handleAddNewProductClick = () => {
        setPurchase((draft) => getPurchaseItemsHavingId(draft));
        history.push("/cart/init", { isAddingItem: true });
    };

    const handleNext = () => {
        const { activeItemIndex } = useCreatePurchaseStore.getState();
        setActiveItemIndex(activeItemIndex + 1);

        const createPurchaseScreenLayout = document.querySelector(`#columnLayoutFill`);
        if (createPurchaseScreenLayout) {
            (createPurchaseScreenLayout as HTMLElement).scroll(0, 0);
        }
    };

    return (
        <CreatePurchaseFooter secondaryText="Cancel" onCancel={handleCancel}>
            {hasDisabledReasons ? (
                <Tooltip
                    content={disabledReasons.map((message) => (
                        <div key={message}>{message}</div>
                    ))}
                >
                    <CheckoutButton disabled label={buttonLabel} />
                </Tooltip>
            ) : shouldShowNextButton ? (
                <Button disabled={shouldDisableFooter} onClick={handleNext}>
                    Next
                </Button>
            ) : (
                <CheckoutButton type="submit" disabled={shouldDisableFooter} onClick={onSubmit} />
            )}

            <SecondaryButton disabled={shouldDisableFooter} onClick={handleAddNewProductClick}>
                Add Another Product
            </SecondaryButton>
        </CreatePurchaseFooter>
    );
};
